import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';

import { Flex, FlexColumn } from 'src/components/flex';
import convertFileSize from 'src/utils/convert-file-size';

interface Props {
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

export default function FileAttachments({
    files,
    setFiles,
}: Props) {
    const handleRemoveFile = (index: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const fileSizeUnit = (size: number) => {
        if (size < 1024) {
            return 'bytes';
        }
        if (size > 1024 * 1024) {
            return 'MB';
        }
        return 'KB';
    };

    const formatName = (name: string) => {
        const length = 15;

        if (name.length <= length) {
            return name;
        }
        return `${name.substring(0, length / 2)}...${name.substring(name.length - 1 - length / 2, name.length)}`;
    };

    const tempUrls = useMemo(() => {
        return files.map((file, index) => ({
            url: URL.createObjectURL(file),
            name: formatName(file.name),
            size: file.size,
            type: file.type,
            index
        }));
    }, [files]);

    return (
        <Flex mobileFlexDirection="column" flexWrap="wrap">
            {tempUrls.map((file) => (
                <Flex
                    justifyContent="space-between"
                    mt={1}
                    p={1}
                    maxWidth="300px"
                    width="100%"
                    key={file.url}
                    sx={{
                        backgroundColor: '#F7F7F7',
                        borderRadius: 4,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    <Flex alignItems="center" width="100%" minWidth={0}>
                        <Flex alignItems="center" flexShrink={0}>
                            {file.type.startsWith('image/') ?
                                (
                                    <Box
                                        component="img"
                                        src={file.url}
                                        alt={file.name}
                                        sx={{
                                            width: 96,
                                            height: 72,
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                        }}
                                    />
                                ) :
                                (
                                    <Avatar sx={{ bgcolor: '#10B981', width: 56, height: 56 }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                                    </Avatar>
                                )
                            }
                        </Flex>
                        <Flex alignItems="center" flex={1} minWidth={0}>
                            <FlexColumn minWidth={0} flex={1}>
                                <Typography
                                    variant="body2"
                                    width="100%"
                                    fontSize="12px"
                                    sx={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <strong>{file.name}</strong>
                                </Typography>
                                <Typography variant="caption">
                                    {`${convertFileSize(file.size)} ${fileSizeUnit(file.size)}`}
                                </Typography>
                            </FlexColumn>
                            <IconButton onClick={() => handleRemoveFile(file.index)} sx={{ flexShrink: 0 }}>
                                <CloseIcon sx={{ color: '#ADADAD' }} />
                            </IconButton>
                        </Flex>
                    </Flex>
                </Flex>
            ))}
        </Flex>
    );
}
